import BlogSection from "../atoms/BlogPreview";
import ContactForm from "../atoms/ContactForm";
import ServicesHeader from "../atoms/ServicesHeader";
import Button from "../components/v2/Button";
import Input from "../components/v2/Input";
import Textarea from "../components/v2/TextArea";
import Typography from "../components/v2/Typography";
import useApp from "../hooks/useApp";

const PortfolioManagement = () => {

    const appCtx = useApp();

    return (
        <>
            <ServicesHeader active="/services/strategy" />
            {/* <!-- 1. HERO --> */}
            <section className="py-20 md:py-28 bg-white strategy-service">
                <div className="container mx-auto px-4 max-w-6xl grid md:grid-cols-2 gap-12 items-start">
                    <div>
                        <h1 className="text-5xl font-bold leading-tight mb-6">Domain Strategy Services</h1>
                        <p className="text-xl mb-4">Not sure which names to keep, sell, or acquire next?</p>
                        <p className="text-lg mb-6 max-w-xl">We offer clear-eyed, unbiased domain strategy—based on two decades of brokerage experience and real-world sale data. Our consultants advise public companies, investors, and stealth startups alike. Whether you're managing 20 names or 20,000, we can help you align your domain assets with your business goals.</p>
                        <ul className="list-disc list-inside space-y-2 text-lg">
                            <li>Clear-cut valuation reports and sell/keep insights</li>
                            <li>Threat and competition monitoring</li>
                            <li>Custom playbooks for acquisition or divestment</li>
                            <li>Ongoing portfolio oversight and trend reporting</li>
                        </ul>
                    </div>
                    {/* Callback Form */}
                    <div className="border-1 border-blue-500 p-8 shadow-sm bg-gray-50">
                        <h2 className="text-2xl font-semibold mb-4">Send an Inquiry</h2>
                        <form onSubmit={(e) => {
                            e.preventDefault();
                            console.log(e.target);
                            appCtx.submitForm(e.target.name.value, e.target.email.value, e.target.phone.value, e.target.query.value);
                        }} className="space-y-4">
                            <div className="form-control">
                                <div className="form-name">
                                    <Typography variant="para">Name</Typography>
                                </div>
                                <Input isDark={true} name="name" label="" placeholder="John Doe" type="text" value="" onChange={() => { }} />
                            </div>
                            <div className="form-control">
                                <div className="form-name">
                                    <Typography variant="para">Phone number</Typography>
                                </div>
                                <Input isDark={true} name="phone" label="" placeholder="+1 123456789" type="number" value="" onChange={() => { }} />
                            </div>
                            <div className="form-control">
                                <div className="form-name">
                                    <Typography variant="para">Email</Typography>
                                </div>
                                <Input isDark={true} name="email" label="" placeholder="johndoe@gmail.com" type="email" value="" onChange={() => { }} />
                            </div>
                            <Textarea isDark={true} name="query" label="" placeholder="What kind of strategy help do you need?" value="" onChange={() => { }} style={{height: 170}} />
                            <Button variant="primary" type="submit" fullWidth>
                                <Typography variant="button">Submit Inquiry</Typography>
                            </Button>
                        </form>
                        <p className="text-sm mt-2"><strong>Have questions?</strong> Our strategy lead will respond within 24 hours.</p>
                    </div>
                </div>
            </section>

            {/* 2. STRATEGIC ADVISORY AREAS */}
            <section className="py-16 bg-gray-50">
                <div className="container mx-auto px-4 max-w-5xl">
                    <h2 className="text-3xl font-semibold mb-6">Core Strategy Services</h2>
                    <div className="grid md:grid-cols-2 gap-8 text-lg">
                        <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                            <h3 className="text-xl font-semibold mb-2">Portfolio Management</h3>
                            <p>We review your entire portfolio and make evidence-based recommendations—what to keep, what to drop, and what to list. We monitor market activity and help trim bloat that eats renewals.</p>
                        </div>
                        <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                            <h3 className="text-xl font-semibold mb-2">Market Intelligence</h3>
                            <p>We track domain sales, competitor activity, and keyword trends to inform timing and pricing decisions. Our clients receive updates on adjacent sales, inbound interest patterns, and key expiry alerts.</p>
                        </div>
                        <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                            <h3 className="text-xl font-semibold mb-2">Threat Intelligence</h3>
                            <p>We help you identify and mitigate threats from typo-squatters, expired defensive names, and phishing vectors. ccTLD gaps are flagged, and we suggest appropriate recovery or registration actions.</p>
                        </div>
                        <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                            <h3 className="text-xl font-semibold mb-2">Acquisition Strategy</h3>
                            <p>We create target lists, map negotiation routes, and handle outreach. Our clients acquire valuable domains quietly and cost-effectively, supported by third-party valuation analysis.</p>
                        </div>
                        <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                            <h3 className="text-xl font-semibold mb-2">Divestment Strategy</h3>
                            <p>We help you sell high-value names at the right time, with discreet outbound, trusted escrow, and optional listing strategies. Every recommendation is benchmarked to real sales data.</p>
                        </div>
                        <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                            <h3 className="text-xl font-semibold mb-2">Brand Alignment</h3>
                            <p>We ensure your domains match your current and future brand positioning. That means flagging product misalignment, risk of confusion, or unused names better suited for spinouts or partners.</p>
                        </div>
                        <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                            <h3 className="text-xl font-semibold mb-2">Dedicated Support</h3>
                            <p>We act as your domain strategy arm—available for internal check-ins, leadership Q&A, and urgent valuation decisions. Fast response, full NDA, and agenda-driven sessions.</p>
                        </div>
                        <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                            <h3 className="text-xl font-semibold mb-2">Reporting & Tracking</h3>
                            <p>Receive structured reports on portfolio composition, traffic shifts, renewal costs, and market value changes. Perfect for quarterly reviews, budget planning, and board updates.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* 3. ENGAGEMENT OPTION */}
            <section className="py-16 bg-white">
                <div className="container mx-auto px-4 max-w-5xl">
                    <h2 className="text-3xl font-semibold mb-6">Strategy Brief: $3,500</h2>
                    <p className="text-lg mb-4">This one-time engagement includes portfolio review, threat analysis, sales opportunity report, and acquisition roadmap. Most briefs are delivered within two weeks, with a live review session included.</p>
                    <p className="text-sm text-gray-600">*For larger portfolios or custom requests, additional terms may apply.</p>
                </div>
            </section>

            {/* 4. WHY STRATEGY MATTERS */}
            <section className="py-16 bg-gray-50">
                <div className="container mx-auto px-4 max-w-5xl">
                    <h2 className="text-3xl font-semibold mb-6">Why Domain Strategy Matters</h2>
                    <ul className="grid md:grid-cols-2 gap-6 text-lg list-disc list-inside">
                        <li>Portfolio bloat inflates renewal costs & hides saleable assets</li>
                        <li>Unregistered ccTLDs expose brand to phishing & competitor poaching</li>
                        <li>Naming dead‑ends delay product launches and confuse investors</li>
                        <li>Boards need third‑party validation before green‑lighting six‑figure buys</li>
                    </ul>
                </div>
            </section>

            {/* 5. WHAT SETS US APART */}
            <section className="py-16 bg-white">
                <div className="container mx-auto px-4 max-w-5xl">
                    <h2 className="text-3xl font-semibold mb-6">What Sets NameConnect Apart</h2>
                    <ul className="space-y-4 text-lg list-disc list-inside">
                        <li><strong>Two‑Decade Track Record:</strong> From Fortune 500 rebrands to stealth startup acquisitions—we've seen every scenario.</li>
                        <li><strong>Data‑Driven Decisions:</strong> We base recommendations on real sales data, not opinions or gut feelings.</li>
                        <li><strong>Total Confidentiality:</strong> Work under strict NDAs with dedicated point of contact.</li>
                        <li><strong>Actionable Insights:</strong> Every report includes clear next steps and implementation guidance.</li>
                        <li><strong>Global Perspective:</strong> We understand ccTLD markets, international regulations, and cross‑border transactions.</li>
                    </ul>
                </div>
            </section>

            {/* 6. PROCESS */}
            <section className="py-16 bg-gray-50">
                <div className="container mx-auto px-4 max-w-6xl">
                    <h2 className="text-3xl font-semibold mb-8">Our Strategic Process</h2>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                        <div className="space-y-8 text-lg order-2 lg:order-1">
                            <div>
                                <h3 className="text-2xl font-semibold mb-2">1. Discovery & Analysis</h3>
                                <p>We audit your current portfolio, understand your business goals, and identify strategic opportunities.</p>
                            </div>
                            <div>
                                <h3 className="text-2xl font-semibold mb-2">2. Market Research</h3>
                                <p>Deep dive into comparable sales, competitor analysis, and industry trends that affect your domains.</p>
                            </div>
                            <div>
                                <h3 className="text-2xl font-semibold mb-2">3. Strategic Recommendations</h3>
                                <p>Clear action plan with priorities, timelines, and expected outcomes for each recommendation.</p>
                            </div>
                            <div>
                                <h3 className="text-2xl font-semibold mb-2">4. Implementation Support</h3>
                                <p>We can handle the execution—from acquisitions to sales to defensive registrations.</p>
                            </div>
                            <div>
                                <h3 className="text-2xl font-semibold mb-2">5. Ongoing Monitoring</h3>
                                <p>Regular check‑ins and market updates to keep your strategy aligned with evolving goals.</p>
                            </div>
                        </div>
                        <div className="flex justify-center lg:justify-end order-1 lg:order-2">
                            <img 
                                src="https://cdn.webstadia.com/47a1f5a2-6f7d-48e5-a45b-5066015538cb.jpeg" 
                                alt="Strategic Process" 
                                className="w-full max-w-md lg:max-w-lg rounded-lg shadow-lg"
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* 7. CTA */}
            <section className="py-20 bg-blue-600 text-white text-center">
                <div className="container mx-auto px-4 max-w-4xl">
                    <h2 className="text-4xl font-bold mb-4">Let's Optimize Your Portfolio</h2>
                    <p className="text-lg mb-8">Use the inquiry form at the top of this page. We'll reach out within 24 hours.</p>
                    <a href="#" className="inline-block bg-white text-blue-600 font-semibold py-4 px-10 rounded" onClick={(e) => {e.preventDefault(); window.scrollTo({top: 0, behavior: 'smooth'});}}>Get Started Today</a>
                </div>
            </section>

            <BlogSection />
        </>
    );
};

export default PortfolioManagement;